import React from "react";
import { formatDate } from "../../../utils/common";

/**
 * Componente ExpenseGroupCard para mostrar gastos agrupados por categoría
 * 
 * @param {Object} props
 * @param {string} props.title - Título de la tarjeta
 * @param {Array} props.expenses - Array de gastos a mostrar
 * @param {Function} props.formatAmount - Función para formatear montos
 * @param {React.ReactElement} props.icon - Icono a mostrar
 * @param {string} props.color - Clase de gradiente de color
 * @param {Function} props.onEditExpense - Función para editar un gasto
 * @param {Function} props.onDeleteExpense - Función para eliminar un gasto
 */
const ExpenseGroupCard = ({ title, expenses, formatAmount, icon, color, onEditExpense, onDeleteExpense }) => {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  // Misma moneda que los gastos del grupo (evita S/ en bloques USD al omitir currency en formatAmount)
  const groupCurrency = expenses[0]?.currency || "PEN";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
      <div 
        className={`bg-gradient-to-r ${color} p-3 cursor-pointer hover:opacity-95 transition-opacity`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              {React.cloneElement(icon, { className: "w-4 h-4 text-white" })}
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">{title}</h3>
              <p className="text-xs text-white/80">{expenses.length} gastos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-sm font-bold text-white">
                {formatAmount ? formatAmount(total, false, groupCurrency) : total}
              </p>
            </div>
            <svg 
              className={`w-4 h-4 text-white transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {!isCollapsed && (
        <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
          {expenses.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-xs">No hay gastos registrados</p>
            </div>
          ) : (
            expenses.map((expense) => (
              <div
                key={expense.id}
                className="p-2.5 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-gray-800 truncate">
                      {expense.name}
                    </h4>
                    {expense.date && (
                      <p className="text-xs text-gray-500">
                        {formatDate(expense.date)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm text-gray-900">
                      {formatAmount ? formatAmount(expense.amount, false, expense.currency || groupCurrency) : expense.amount}
                    </p>
                    {expense.currency && expense.currency !== "PEN" && (
                      <span className="text-gray-500 text-xs">({expense.currency})</span>
                    )}
                    
                    {/* Botones de acción */}
                    <div className="flex items-center gap-0.5">
                      {/* Botón editar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditExpense && onEditExpense(expense.id);
                        }}
                        className="p-1.5 hover:bg-blue-100 rounded transition-colors text-blue-600"
                        aria-label="Editar gasto"
                        title="Editar"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      {/* Botón eliminar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteExpense && onDeleteExpense(expense.id, expense.name);
                        }}
                        className="p-1.5 hover:bg-red-100 rounded transition-colors text-red-600"
                        aria-label="Eliminar gasto"
                        title="Eliminar"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseGroupCard;
